import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { IFilter, IProjectFilteredList } from '../components/organisms/FilteredProjectsGallery/FilteredProjectsGallery.types';
import { TProject } from '@/types/global';
import { IProjectsIDsResponse } from '@/directus/services/common/projects-ids/fetch';

const isSameFilter = (a: IFilter, b: IFilter) =>
  a.id === b.id && a.collection === b.collection;

export type TTriggerer = 'ui' | 'resetUi' | 'resetInitial' | 'restore' | 'restore-only-filters';

export interface ISelectedFilters {
  filters: IFilter[];
  triggerer: TTriggerer;
}

export interface TImageSearch {
  value: string;
  triggerer: TTriggerer;
}

export interface ISearch {
  value: string;
  triggerer: TTriggerer;
}

export interface IProjectsNavigation {
  breadcrumb: string;
  projects: { id: string; title: string }[];
}

type FiltersState = {
  search: ISearch;
  selectedFilters: ISelectedFilters;
  areFiltersOpen: boolean;
  // currentParamsString: string;
  projectsIDs?: IProjectsIDsResponse;
  isProjectsLazyLoadFinished: {
    isFinished: boolean;
    triggerer: 'resetInitial' | 'initialFetch' | 'endLoad';
  };
  locationFilterNumActive?: number;
  firstFetchFilteredProjects: IProjectFilteredList[] | undefined;
  lazyLoadedProjectsLists?: IProjectFilteredList[];
  nextBufferLazyLoadedProjects?: TProject[] | undefined;
  archivedFilteredProjects?: TProject[] | undefined;
  presentationProjects?: TProject[];
  paginationLazyLoad: number;
  isCurrentlyFetchingProjects: boolean;
  imagesWithProjects?: TProject[];
  imageSearch: TImageSearch;
  projectCount?: {
    archived: number;
    notArchived: number;
  };
  previousClickedLinkProject?: string;
  isRestoringState?: boolean;
  filtersFromUrl?: IFilter[];
  savedFilteredProjectsState?: {
    selectedFilters: IFilter[];
    search?: ISearch;
    imageSearch: TImageSearch;
    path: string;
    firstFetchFilteredProjects: IProjectFilteredList[] | undefined;
    lazyLoadedProjectsLists?: IProjectFilteredList[];
    nextBufferLazyLoadedProjects?: TProject[] | undefined;
    paginationLazyLoad: number;
    isProjectsLazyLoadFinished: {
      isFinished: boolean;
      triggerer: 'resetInitial' | 'initialFetch' | 'endLoad';
    };
    archivedFilteredProjects?: TProject[] | undefined;
    projectCount?: {
      archived: number;
      notArchived: number;
    };
    projectsIDs?: IProjectsIDsResponse; 
    imagesWithProjects?: TProject[];
    areFiltersOpen?: boolean;
    presentationProjects?: TProject[];
    isArchivedProjFetchFinished?: boolean;
  },
  hasStateBeenRestored?: boolean;
  projectsNavigation?: IProjectsNavigation;
  currentProjectNextPrevious?: {
    previous: TProject | undefined;
    next: TProject | undefined;
  };
  isArchivedProjFetchFinished: boolean;
  setSearch: (title: string) => void;
  toggleFilter: (filter: IFilter) => void;
  // setFiltersFromUrl: (filters?: IFilter[], title?: string) => void;
  setCurrentParamsString: (paramsString: string) => void;
  removeFilters: (filters: IFilter[]) => void;
  toggleAllFilters: (filters: IFilter[]) => void;
  toggleFilterMenu: () => void;
  setProjectsIDs: (ids: IProjectsIDsResponse) => void;
  setLocationFilterNumActive: (num: number) => void;
  setFirstFetchFilteredProjects: (projects: IProjectFilteredList[] | undefined) => void;
  setLazyLoadedProjectsLists: (projects: IProjectFilteredList[]) => void;
  setArchivedFilteredProjects: (archivedFilteredProjects: TProject[]) => void;
  setNextBufferLazyLoadedProjects: (nextBuffer: TProject[] | undefined) => void;
  setPaginationLazyLoad: (pagination: number) => void;
  setIsProjectsLazyLoadFinished: (isProjectsLazyLoadFinished: {
    isFinished: boolean;
    triggerer: 'resetInitial' | 'initialFetch' | 'endLoad';
  }) => void;
  setImagesWithProjects: (imagesWithProjects: TProject[]) => void;
  restartLazyLoadedProjectsLists: () => void;
  setImageSearch: ({value, triggerer}: TImageSearch) => void;
  resetSelectedFilters: () => void;
  setProjectCount: ({ archived, notArchived }: { archived: number; notArchived: number }) => void;
  restoreSavedFilteredProjectsState: () => void;
  restoreOnlyFiltersState: () => void;
  saveFilteredProjectsState: (
    path: string
  ) => void;
  resetFiltersAndProjects: () => void;
  setProjectsNavigation: (value: IProjectsNavigation) => void;
  setCurrentProjectNextPrevious: (value: {
    previous: TProject | undefined;
    next: TProject | undefined;
  }) => void;
  setPresentationProjects: (projects: TProject[]) => void;
  setIsArchivedProjFetchFinished: (value: boolean) => void;
};

export const useFiltersStore = create<FiltersState>()(
  devtools((set, get) => {
    const safeSet = <K extends keyof FiltersState>(key: K, value: FiltersState[K]) => {
      const currentValue = get()[key];
      const hasChanged = Array.isArray(currentValue) && Array.isArray(value)
        ? !shallow(currentValue, value)
        : currentValue !== value;

      if (hasChanged) {
        set({ [key]: value } as Pick<FiltersState, K>, false, `set_${key.toString()}`);
      }
    };

    const setProjectsIDs = (ids: IProjectsIDsResponse) => {
      const current = get().projectsIDs;
      const hasChanged =
        !current ||
        !shallow(current.archived, ids.archived) ||
        !shallow(current.notArchived, ids.notArchived);

      if (hasChanged) {
        set({ projectsIDs: ids }, false, 'set_projectsIDs');
      }
    };

    return {
      title: '',
      selectedFilters: [],
      areFiltersOpen: false,
      // currentParamsString: '',
      projectsIDs: undefined,
      isProjectsLazyLoadFinished: {
        isFinished: false,
        triggerer: 'resetInitial',
      },
      locationFilterNumActive: 0,
      locationSubfilterNumFromFilter: undefined,
      lazyLoadedProjectsLists: [],
      firstFetchFilteredProjects: undefined,
      nextBufferLazyLoadedProjects: undefined,
      paginationLazyLoad: 2,
      isCurrentlyFetchingProjects: false,
      imageSearch: '',
      imagesWithProjects: [],
      projectCount: undefined,
      previousClickedLinkProject: undefined,
      savedFilteredProjectsState: undefined,
      hasStateBeenRestored: false,
      isRestoringState: false,
      isArchivedProjFetchFinished: false,

      setIsArchivedProjFetchFinished: (value) => {
        set({ isArchivedProjFetchFinished: value }, false, 'set_isArchivedProjFetchFinished');
      },

      setProjectCount: ({ archived, notArchived }) => {
        set({ projectCount: { archived, notArchived } }, false, 'set_projectCount');
      },

      setImageSearch: ({value, triggerer}: TImageSearch) => {
        safeSet('imageSearch', { value, triggerer });
      },

      // setImagesWithProjects: (imagesWithProjects) => {
      //   safeSet('imagesWithProjects', imagesWithProjects);
      // },

      setImagesWithProjects: (imagesWithProjects: TProject[]) => {
        safeSet('imagesWithProjects', imagesWithProjects);
      },

      restartLazyLoadedProjectsLists: () => {
        set({ lazyLoadedProjectsLists: [] }, false, 'reset_lazyLoadedProjectsLists');
        set({ nextBufferLazyLoadedProjects: undefined }, false, 'reset_nextBufferLazyLoadedProjects');
      },

      setFirstFetchFilteredProjects: (projects) => set(
        { firstFetchFilteredProjects: projects }, 
        false, 
        'set_firstFetchFilteredProjects'
      ),

      setArchivedFilteredProjects: (archivedFilteredProjects) => {
        set({ archivedFilteredProjects }, false, 'set_archivedFilteredProjects');
      },

      setLocationFilterNumActive: (num) => {
        safeSet('locationFilterNumActive', num);
      },

      setProjectsIDs: (ids) => {
        setProjectsIDs(ids);
      },

      setSearch: (search) => safeSet('search', {
        value: search,
        triggerer: 'ui',
      }),

      toggleFilter: (filter) => {
        const selectedFilters = get().selectedFilters || [];
        const current = selectedFilters.filters || [];
        const exists = current.some((f) => isSameFilter(f, filter));
        const updated = exists
          ? current.filter((f) => !isSameFilter(f, filter))
          : [...current, filter];

        safeSet('selectedFilters', {
          filters: updated,
          triggerer: 'ui',
        });
      },

      removeFilters: (toRemove) => {
        const current = get().selectedFilters.filters;
        const updated = current.filter(
          (f) => !toRemove.some((r) => isSameFilter(f, r))
        );
        safeSet('selectedFilters', {
          filters: updated,
          triggerer: 'ui',
        });
      },

      toggleAllFilters: (newFilters) => {
        const current = get().selectedFilters.filters;
        const allSelected = newFilters.every((nf) =>
          current.some((f) => isSameFilter(f, nf))
        );

        const updated = allSelected
          ? current.filter(
              (f) => !newFilters.some((nf) => isSameFilter(f, nf))
            )
          : [...current, ...newFilters.filter(
              (nf) => !current.some((f) => isSameFilter(f, nf))
            )];

        safeSet('selectedFilters', {
          filters: updated,
          triggerer: 'ui',
        });
      },

      // setFiltersFromUrl: (filters = [], title = '') => {
      //   safeSet('selectedFilters', {
      //     filters,
      //     triggerer: 'resetInitial',
      //   });
      //   safeSet('title', title);
      // },

      toggleFilterMenu: () => {
        const current = get().areFiltersOpen;
        safeSet('areFiltersOpen', !current);
      },

      setPresentationProjects: (projects: TProject[]) => {
        safeSet('presentationProjects', projects);
      },

      // setCurrentParamsString: (paramsString: string) => {
      //   safeSet('currentParamsString', paramsString);
      // },

      setLazyLoadedProjectsLists: (items?: IProjectFilteredList[] | null) =>
        set(
          (state) => ({
            lazyLoadedProjectsLists:
              items && items.length > 0
                ? [...(state.lazyLoadedProjectsLists ?? []), ...items]
                : [],
          }),
          false,
          items && items.length > 0
            ? 'append_lazyLoadedProjectsLists'
            : 'clear_lazyLoadedProjectsLists'
        ),

      setNextBufferLazyLoadedProjects: (nextBufferLazyLoadedProjects) =>
        set(
          { nextBufferLazyLoadedProjects: nextBufferLazyLoadedProjects },
          false,
          'set_nextBufferLazyLoadedProjects'
        ),

      setPaginationLazyLoad: (paginationLazyLoad) => {
        safeSet('paginationLazyLoad', paginationLazyLoad);
      },

      setIsProjectsLazyLoadFinished: ({ isFinished, triggerer }) => {
        set({ isProjectsLazyLoadFinished: { isFinished, triggerer } }, false, 'set_isProjectsLazyLoadFinished');
      },

      resetSelectedFilters: () => safeSet('selectedFilters', {
        filters: [],
        triggerer: 'resetUi',
      }),

      /* Restores state logic to be used when navigating back to the filtered projects page */
      restoreSavedFilteredProjectsState: () => {
        const { savedFilteredProjectsState: s } = get();
        if (!s) return;

        set({ isRestoringState: true }, false, 'restore_start');

        safeSet('search', { value: s.search?.value ?? '', triggerer: 'restore' });
        safeSet('imageSearch', { value: s.imageSearch.value ?? '', triggerer: 'restore' });
        safeSet('selectedFilters', {
          filters: s.selectedFilters ?? [],
          triggerer: 'restore',
        });
        safeSet('paginationLazyLoad', s.paginationLazyLoad ?? 2);
        set({ isProjectsLazyLoadFinished: s.isProjectsLazyLoadFinished }, false, 'restore_isProjectsLazyLoadFinished');
        set({ firstFetchFilteredProjects: s.firstFetchFilteredProjects }, false, 'restore_firstFetchFilteredProjects');
        set({ lazyLoadedProjectsLists: s.lazyLoadedProjectsLists ?? [] }, false, 'restore_lazyLoadedProjectsLists');
        set({ nextBufferLazyLoadedProjects: s.nextBufferLazyLoadedProjects }, false, 'restore_nextBufferLazyLoadedProjects');
        set({ isArchivedProjFetchFinished: s.isArchivedProjFetchFinished }, false, 'restore_isArchivedProjFetchFinished');

        if (s.archivedFilteredProjects) {
          set({ archivedFilteredProjects: s.archivedFilteredProjects }, false, 'restore_archivedFilteredProjects');
        }

        if (s.imagesWithProjects) {
          set({ imagesWithProjects: s.imagesWithProjects }, false, 'restore_imagesWithProjects');
        }

        if (s.projectCount) {
          set({ projectCount: s.projectCount }, false, 'restore_projectCount');
        }

        if (s.projectsIDs) {
          (get().setProjectsIDs as (ids: IProjectsIDsResponse) => void)(s.projectsIDs);
        }

        if (s.areFiltersOpen !== undefined) {
          safeSet('areFiltersOpen', s.areFiltersOpen);
        }
      },

      restoreOnlyFiltersState: () => {
        const { savedFilteredProjectsState: s } = get();
        if (!s) return;
        safeSet('selectedFilters', {
          filters: s.selectedFilters ?? [],
          triggerer: 'restore-only-filters',
        });
        safeSet('search', { value: s.search?.value ?? '', triggerer: 'restore-only-filters' });
        safeSet('imageSearch', { value: s.imageSearch.value ?? '', triggerer: 'restore-only-filters' });
        if (s.areFiltersOpen !== undefined) {
          safeSet('areFiltersOpen', s.areFiltersOpen);
        }
      },

      resetFiltersAndProjects: () => {
        useFiltersStore.setState({ hasStateBeenRestored: false });
        useFiltersStore.setState({ selectedFilters: { filters: [], triggerer: 'resetInitial' } });
        useFiltersStore.setState({ firstFetchFilteredProjects: undefined });
        useFiltersStore.setState({ lazyLoadedProjectsLists: [] });
        useFiltersStore.setState({ nextBufferLazyLoadedProjects: undefined });
        useFiltersStore.setState({ projectsIDs: undefined });
        useFiltersStore.setState({ isProjectsLazyLoadFinished: { isFinished: false, triggerer: 'resetInitial' } });
        useFiltersStore.setState({ archivedFilteredProjects: [] });
        useFiltersStore.setState({ projectCount: undefined });
        useFiltersStore.setState({ paginationLazyLoad: 2 });
        useFiltersStore.setState({ imageSearch: { value: '', triggerer: 'resetInitial' } });
        useFiltersStore.setState({ imagesWithProjects: [] });
        useFiltersStore.setState({ search: { value: '', triggerer: 'resetInitial' } });
        // useFiltersStore.setState({ isArchivedProjFetchFinished: false });
      },

      saveFilteredProjectsState: (path: string) => {
        const st = get();
        const snapshot = {
          selectedFilters: st.selectedFilters.filters,
          search: st.search,
          imageSearch: st.imageSearch,
          path,
          firstFetchFilteredProjects: st.firstFetchFilteredProjects,
          lazyLoadedProjectsLists: st.lazyLoadedProjectsLists,
          nextBufferLazyLoadedProjects: st.nextBufferLazyLoadedProjects,
          paginationLazyLoad: st.paginationLazyLoad,
          isProjectsLazyLoadFinished: st.isProjectsLazyLoadFinished,
          archivedFilteredProjects: st.archivedFilteredProjects,
          projectCount: st.projectCount,
          projectsIDs: st.projectsIDs,
          imagesWithProjects: st.imagesWithProjects,
          isArchivedProjFetchFinished: st.isArchivedProjFetchFinished,
        } as FiltersState['savedFilteredProjectsState'];

        set({ savedFilteredProjectsState: snapshot }, false, 'save_savedFilteredProjectsState');
      },

      setProjectsNavigation: ({ breadcrumb, projects }: IProjectsNavigation) => set({ projectsNavigation: { breadcrumb, projects } }, false, 'setProjectsNavigation'),
      setCurrentProjectNextPrevious: ({ previous, next }) => set({ currentProjectNextPrevious: { previous, next } }, false, 'setCurrentProjectNextPrevious'),
    };
  }, { name: 'FiltersStore' }) // <-- Name for Redux DevTools
);


    // setLocationSubfilterNumFromFilter: (num: number[] | undefined) => {
    //   safeSet('locationSubfilterNumFromFilter', num);
    // },

    // addSelectedLocationFromSubfilterNum: (num: number) => {
    //   const current = get().locationSubfilterNumFromFilter ?? [];
    //   if (!current.includes(num)) {
    //     safeSet('locationSubfilterNumFromFilter', [...current, num]);
    //   }
    // },

    // removeSelectedLocationFromSubfilterNum: (num: number) => {
    //   const current = get().locationSubfilterNumFromFilter ?? [];
    //   if (current.includes(num)) {
    //     safeSet('locationSubfilterNumFromFilter', current.filter(n => n !== num));
    //   }
    // },